import React, { useState, useContext, useEffect } from 'react';
import { CurpContext } from '../context/CurpContext';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '../components/Logo';
import BackgroundLayout from '../components/BackgroundLayout';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import Loader from '../components/Loader';

const AppointmentPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { curp } = useContext(CurpContext);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [availableDates, setAvailableDates] = useState([]);
    const [availableTimes, setAvailableTimes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    dayjs.locale('es');

    useEffect(() => {
        const fetchSchedule = async () => {
            setIsLoading(true);
            let service_type = '';
            if (location.pathname.includes('/services/counseling') || location.pathname.includes('/services/self-test')) {
                service_type = 'detection';
            } else if (location.pathname.includes('/services/medical-service') || location.pathname.includes('/services/vaccine')) {
                service_type = 'medical';
            } else {
                return;
            }

            try {
                const response = await fetch('https://admin-django-inspira.azurewebsites.net/api/services/get-schedule/', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ service_type }),
                });

                const data = await response.json();

                if (service_type === 'detection') {
                    const parsedDates = Object.keys(data).map(dateStr => ({
                        date: dayjs(dateStr),
                        times: data[dateStr],
                    }));
                    setAvailableDates(parsedDates);
                } else if (service_type === 'medical' && data.next_available) {
                    setAvailableDates([{ date: dayjs(data.next_available.date), times: [data.next_available.time] }]);
                } else {
                    setAvailableDates([]);
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchSchedule();
    }, [location.pathname]);

    const handleDateClick = (dateObj) => {
        setSelectedDate(dateObj.date);
        setSelectedTime(null);
        setAvailableTimes(dateObj.times);
    };

    const handleTimeClick = (time) => {
        setSelectedTime(time);
    };

    const getServiceField = () => {
        if (location.pathname.includes('/services/counseling')) {
            return 'DETECCIÓN';
        } else if (location.pathname.includes('/services/self-test')) {
            return 'AUTO PRUEBA';
        } else if (location.pathname.includes('/services/medical-service')) {
            return 'SERVICIO MEDICO';
        } else if (location.pathname.includes('/services/vaccine')) {
            return 'VACUNA';
        }
        return '';
    };

    const handleSubmit = async () => {
        if (!selectedDate || !selectedTime) {
            toast.error('Por favor selecciona una fecha y un horario');
            return;
        }

        const service = getServiceField();
        const body = {
            curp,
            service,
            date: selectedDate.format('YYYY-MM-DD'),
            time: selectedTime,
        };

        toast.promise(
            fetch('https://admin-django-inspira.azurewebsites.net/api/services/register-appointment/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            }).then(async response => {
                if (response.ok) {
                    const data = await response.json();
                    // El id de la cita es el mismo UUID que codifica el QR del correo
                    // y el que espera el escáner (confirm-attendance).
                    navigate(data.id ? `/thank-you?cita=${data.id}` : '/thank-you');
                    return '¡Lo hiciste! Y sin romper nada 👌';
                } else {
                    const errorData = await response.json();
                    throw new Error(errorData.error || errorData.message || '¡Error! Parece que algo no le gustó a la tecnología. 🙄');
                }
            }),
            {
                loading: 'Un segundo… Prometemos que no estamos viendo memes 🙈',
                success: '¡Lo hiciste! Y sin romper nada 👌',
                error: (err) => `Error: ${err.message || '¡Error! Parece que algo no le gustó a la tecnología. 🙄'}`,
            }
        );
    };

    return (
        <BackgroundLayout className="px-8 flex flex-col items-center">
            <motion.div
                className="w-full text-left mt-0 lg:w-2/3 lg:mb-10"
                initial="hidden"
                animate="visible"
            >
                <Logo />
                <motion.h1
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className='text-5xl font-bold text-[#f8485e] mt-10 uppercase drop-shadow-sm'
                >
                    Momento de agendar 🗓️
                </motion.h1>
                <motion.h1 className="text-base text-justify font-bold mt-5 mb-5 text-gray-800 uppercase drop-shadow-sm">
                    Fechas disponibles:
                </motion.h1>

                {isLoading ? (
                    <Loader text="Consultando fechas disponibles..." />
                ) : (
                    <div className="grid grid-cols-4 gap-4">
                        {availableDates.map((dateObj, index) => (
                            <motion.button
                                key={index}
                                className={`p-2 border-2 rounded-md drop-shadow-md ${selectedDate && selectedDate.isSame(dateObj.date, 'day')
                                    ? 'bg-inspiraPink text-white'
                                    : 'bg-white text-inspiraPink border-inspiraPink'
                                    }`}
                                onClick={() => handleDateClick(dateObj)}
                            >
                                {dateObj.date.format('DD ddd')}
                            </motion.button>
                        ))}
                    </div>
                )}
                {selectedDate && availableTimes.length > 0 && (
                    <>
                        <motion.h1 className="text-base text-justify font-bold mt-5 mb-5 text-gray-800 uppercase drop-shadow-sm">
                            Horarios disponibles:
                        </motion.h1>
                        <div className="grid grid-cols-3 gap-4 mb-5">
                            {availableTimes.map((time, index) => (
                                <motion.button
                                    key={index}
                                    className={`p-2 border-2 rounded-md drop-shadow-md ${selectedTime === time
                                        ? 'bg-inspiraPink text-white'
                                        : 'bg-white text-inspiraPink border-inspiraPink'
                                        }`}
                                    onClick={() => handleTimeClick(time)}
                                >
                                    {time}
                                </motion.button>
                            ))}
                        </div>
                    </>
                )}
                {selectedDate && selectedTime && (
                    <motion.div className='flex justify-center mt-10 mb-10'>
                        <button
                            className="mt-5 p-2 bg-inspiraPink text-white rounded-md drop-shadow-md w-2/3"
                            onClick={handleSubmit}
                        >
                            Enviar
                        </button>
                    </motion.div>
                )}
            </motion.div>
        </BackgroundLayout>
    );
};

export default AppointmentPage;
